import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchEvents, type PublicEvent } from '../webinars/api';
import { useSectionVisibility } from '../lib/cms';

type Kind = 'upcoming' | 'recorded';

interface Featured {
  event: PublicEvent;
  kind: Kind;
}

/** Picks the nearest upcoming event, else the latest recording, else nothing
 *  (mirrors `hasVisibleEvent` in api/_lib/liveEvents.ts — `upcoming` already
 *  arrives sorted ascending by start time, `recorded` newest-first). */
function pickFeatured(res: { upcoming: PublicEvent[]; recorded: PublicEvent[] }): Featured | null {
  if (res.upcoming.length > 0) return { event: res.upcoming[0], kind: 'upcoming' };
  if (res.recorded.length > 0) return { event: res.recorded[0], kind: 'recorded' };
  return null;
}

/** Homepage teaser strip for the /webinare funnel. Renders nothing when the
 *  section is hidden via the CMS toggle, when the fetch fails, or when there
 *  is no visible event — a DB problem here must never break the homepage
 *  (same rule as the public listing's own degrade-to-empty behaviour). */
export function WebinarsTeaser() {
  const { t, i18n } = useTranslation();
  const showSection = useSectionVisibility();
  const visible = showSection('webinars');
  const [featured, setFeatured] = useState<Featured | null>(null);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    fetchEvents()
      .then((res) => {
        /* v8 ignore next -- unmount race: cancelled flips only if the component
           unmounts mid-fetch, which the suite does not exercise deterministically */
        if (cancelled) return;
        setFeatured(pickFeatured(res));
      })
      .catch(() => {
        /* v8 ignore next -- unmount race, see above */
        if (cancelled) return;
        setFeatured(null); // silent — a DB problem must not break the homepage
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  if (!visible || !featured) return null;

  const { event, kind } = featured;
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'full', timeStyle: 'short' })
    .format(new Date(event.starts_at));
  const kindKey = kind === 'upcoming' ? 'webinars.upcoming' : 'webinars.recordings';
  const ctaKey = kind === 'upcoming' ? 'webinars.detail' : 'webinars.watch';

  return (
    <section id="webinars" className="webinars-teaser" aria-labelledby="webinars-teaser-title">
      <div className="container webinars-teaser-inner">
        <div className="webinars-teaser-text">
          <p className="section-eyebrow" data-i18n="webinars.title">{t('webinars.title')}</p>
          <h2 className="section-title" id="webinars-teaser-title" data-i18n="webinars.subtitle">{t('webinars.subtitle')}</h2>
          <p className="webinars-teaser-event">
            <span className="webinars-teaser-kind" data-i18n={kindKey}>{t(kindKey)}</span>{' '}
            <strong>{event.title}</strong>
            {kind === 'upcoming' && <span className="webinars-teaser-date"> · {date}</span>}
          </p>
        </div>
        <a className="btn-primary webinars-teaser-cta" href="/webinare" data-i18n={ctaKey}>{t(ctaKey)}</a>
      </div>
    </section>
  );
}
